import React from 'react'
import filterUserCallback from '../../../../functions/shared/users/filterUserCallback'
import { type User } from '../../../../store/slices/userSlice'
import Avatar from '../../../shared/elements/Avatar'
import { type ManualUserOption } from './manualOperations.types'

const toSearchText = (...values: Array<string | number | null | undefined>): string =>
    values
        .filter((value): value is string | number => value != null)
        .map((value) => String(value).toLowerCase())
        .join(' ')

const getUserDisplayName = (user: User): string => {
    const firstName = user.ContactInfo?.FirstName ?? user.Type?.[0]?.Name ?? ''
    const lastName = user.ContactInfo?.FamilyName ?? ''
    const fullName = `${firstName} ${lastName}`.trim()
    return fullName !== '' ? fullName : user.username ?? `User #${String(user.id)}`
}

const toUserOption = (user: User): ManualUserOption => {
    const displayName = getUserDisplayName(user)
    const email = user.email ?? user.ContactInfo?.Email ?? ''
    const phone = user.phone ?? user.ContactInfo?.Phone ?? ''
    const avatar = user.Type?.[0]?.Photo ?? user.ContactInfo?.Avatar ?? undefined
    return {
        user,
        displayName,
        email,
        phone,
        avatar,
        subtitle: [email, phone, user.Category].filter(Boolean).join(' | ')
    }
}

export const ManualUserSelector = ({
    users,
    selectedUserId,
    disabled,
    onSelect
}: {
    users: User[]
    selectedUserId: number | null
    disabled: boolean
    onSelect: (user: User) => void
}): JSX.Element => {
    const [search, setSearch] = React.useState('')
    const userOptions = React.useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase()
        return users
            .filter(filterUserCallback)
            .map(toUserOption)
            .filter((option) => {
                if (normalizedSearch === '') return true
                return toSearchText(
                    option.displayName,
                    option.email,
                    option.phone,
                    option.user.username,
                    option.user.id
                ).includes(normalizedSearch)
            })
            .slice(0, 12)
    }, [search, users])

    return (
        <div className="manual-viewing__user-selector">
            <label className="form-label" htmlFor="manual-viewing-user-search">Search user</label>
            <input
                id="manual-viewing-user-search"
                className="form-control form-control-sm"
                type="search"
                value={search}
                disabled={disabled}
                placeholder="Name, email, phone"
                onChange={(event) => setSearch(event.target.value)}
            />
            <div className="manual-viewing__user-results">
                {userOptions.map((option) => {
                    const isSelected = option.user.id === selectedUserId
                    return (
                        <button
                            className={`manual-viewing__user-result${isSelected ? ' manual-viewing__user-result--selected' : ''}`}
                            key={option.user.id}
                            type="button"
                            disabled={disabled}
                            onClick={() => onSelect(option.user)}
                        >
                            <span className="manual-viewing__user-avatar" aria-hidden="true">
                                <Avatar image={option.avatar} size="34px" />
                            </span>
                            <span className="manual-viewing__user-result-content">
                                <span>{option.displayName}</span>
                                <small>{option.subtitle !== '' ? option.subtitle : `User #${String(option.user.id)}`}</small>
                            </span>
                        </button>
                    )
                })}
                {userOptions.length === 0 && (
                    <div className="manual-viewing__empty-results">No users found.</div>
                )}
            </div>
        </div>
    )
}
